/** Настройки отображения графика (видимость серий + тип отрисовки). */

export const CHART_TYPE_OPTIONS = [
    { id: 'line', label: 'Линии' },
    { id: 'area', label: 'Область' },
    { id: 'bar', label: 'Столбцы' },
    { id: 'stacked-bar', label: 'Стек' },
    { id: 'pie', label: 'Пирог' },
]

export const CHARTABLE_TYPES = new Set(['compare-chart', 'market-chart'])

const PIE_PALETTE = ['#E30613', '#198FA0', '#F5A623', '#7B61FF', '#0F6E56', '#c0c0c0', '#E8A0BF', '#6B8E23']

export function defaultChartViewConfig() {
    return {
        chartType: 'line',
        hiddenSeries: [],
    }
}

export function normalizeChartViewConfig(raw) {
    const base = defaultChartViewConfig()
    if (!raw || typeof raw !== 'object') return { ...base, hiddenSeries: [] }
    const hidden = Array.isArray(raw.hiddenSeries)
        ? raw.hiddenSeries.map(String)
        : []
    const chartType = CHART_TYPE_OPTIONS.some(o => o.id === raw.chartType)
        ? raw.chartType
        : base.chartType
    return {
        chartType,
        hiddenSeries: hidden,
    }
}

/**
 * Категориальная ли ось X (рейтинг марок/регионов), а не временной ряд.
 *
 * Эвристика: если большинство подписей НЕ похожи на период (месяц/год/квартал/
 * дату) — считаем ось категориальной. Для таких графиков линия бессмысленна
 * (соединять LADA→Toyota нечем), интуитивный дефолт — столбцы.
 */
const TIME_LIKE_X_RE = /^(янв|фев|мар|апр|май|июн|июл|авг|сен|окт|ноя|дек|20\d{2}|q[1-4]|\d{1,2}[.\-/]\d{2,4})/i

export function isCategoricalAxis(payload) {
    const labels = payload?.xLabels || []
    if (labels.length < 2) return false
    const timeish = labels.filter(l => TIME_LIKE_X_RE.test(String(l ?? '').trim())).length
    return timeish * 2 < labels.length
}

/**
 * Интуитивный дефолтный тип графика для карточки.
 *
 * Приоритет — текущий выбор пользователя/модели (всё, что не 'line', считаем
 * осознанным). Если тип не задан или это дефолтный 'line', а ось категориальная
 * (рейтинг) — предлагаем 'bar' вместо бессмысленной линии.
 */
export function inferDefaultChartType(card) {
    const current = card?.viewConfig?.chartType
    if (current && current !== 'line' && CHART_TYPE_OPTIONS.some(o => o.id === current)) {
        return current
    }
    return isCategoricalAxis(card?.payload) ? 'bar' : 'line'
}

export function seriesKey(series, idx, legendItem) {
    const fromLegend = legendItem?.id || legendItem?.name
    const fromSeries = series?.id
    return String(fromSeries || fromLegend || idx)
}

export function seriesTotal(series) {
    return (series?.points || []).reduce((sum, p) => sum + (Number(p) || 0), 0)
}

/** Для pie: убрать год/датасет из подписи, оставить бренд. */
export function normalizeBrandForPie(label) {
    if (!label) return ''
    return String(label)
        .replace(/\s*[·•]\s*\d{4}\s*$/i, '')
        .replace(/\s+20\d{2}\s*$/i, '')
        .trim()
}

/**
 * Pie из формы «одна серия, категории на оси X».
 *
 * Типичный рейтинг «Топ-марок»: SELECT brand, sum(registrations) GROUP BY brand
 * → xCol=brand, одна агрегирующая серия «Всего», бренды лежат в xLabels.
 * Тогда каждая (xLabel, point) — это отдельная долька пирога.
 */
function buildPieFromCategoricalAxis(payload) {
    const xLabels = payload.xLabels || []
    const points = payload.series?.[0]?.points || []
    const sorted = xLabels
        .map((label, i) => ({
            label: String(label ?? '').trim() || `#${i + 1}`,
            value: Number(points[i]) || 0,
        }))
        .filter(slice => slice.value > 0)
        .sort((a, b) => b.value - a.value)

    if (!sorted.length) return payload

    const series = sorted.map((slice, i) => ({
        id: slice.label,
        color: PIE_PALETTE[i % PIE_PALETTE.length],
        points: [slice.value],
    }))
    const legend = sorted.map((slice, i) => ({
        id: slice.label,
        name: slice.label,
        label: slice.label,
        color: PIE_PALETTE[i % PIE_PALETTE.length],
    }))

    return {
        ...payload,
        xLabels: [''],
        series,
        legend,
    }
}

/**
 * Pie: одна доля = один бренд, значение = сумма регистраций за период
 * (не отдельные серии «LADA 2025» / «LADA 2026»).
 */
export function buildPieDisplayPayload(payload) {
    if (!payload?.series?.length) return payload

    // Одна серия + несколько категорий на оси X (рейтинг марок/регионов):
    // дольки берём из точек этой серии, а не из самих серий.
    if (payload.series.length === 1 && (payload.xLabels || []).length > 1) {
        return buildPieFromCategoricalAxis(payload)
    }

    const legend = payload.legend || []
    const brandTotals = new Map()
    const brandColors = new Map()

    for (let idx = 0; idx < payload.series.length; idx += 1) {
        const s = payload.series[idx]
        const item = legend[idx] || {}
        const rawName = item.label || item.name || s.id || String(idx)
        const brand = normalizeBrandForPie(rawName)
        if (!brand) continue
        const total = seriesTotal(s)
        brandTotals.set(brand, (brandTotals.get(brand) || 0) + total)
        if (!brandColors.has(brand)) {
            brandColors.set(brand, item.color || s.color)
        }
    }

    const sorted = [...brandTotals.entries()].sort((a, b) => b[1] - a[1])
    const series = sorted.map(([brand, total], i) => ({
        id: brand,
        color: brandColors.get(brand) || PIE_PALETTE[i % PIE_PALETTE.length],
        points: [total],
    }))
    const leg = sorted.map(([brand], i) => ({
        id: brand,
        name: brand,
        label: brand,
        color: brandColors.get(brand) || PIE_PALETTE[i % PIE_PALETTE.length],
    }))

    return {
        ...payload,
        xLabels: [''],
        series,
        legend: leg,
    }
}

/** Payload для отрисовки с учётом типа графика. */
export function resolveChartPayload(payload, viewConfig) {
    const cfg = normalizeChartViewConfig(viewConfig)
    if (cfg.chartType === 'pie') {
        return buildPieDisplayPayload(payload)
    }
    return payload
}

export function allSeriesKeys(payload) {
    const legend = payload?.legend || []
    return (payload?.series || []).map((s, idx) => seriesKey(s, idx, legend[idx]))
}

export function buildDisplaySeries(payload, { hiddenSeries = [] } = {}) {
    const hidden = new Set((hiddenSeries || []).map(String))
    const legend = payload?.legend || []
    return (payload?.series || [])
        .map((series, idx) => ({
            series,
            idx,
            key: seriesKey(series, idx, legend[idx]),
            total: seriesTotal(series),
        }))
        .filter(entry => !hidden.has(entry.key))
}

export function toggleHiddenSeries(viewConfig, key) {
    const cfg = normalizeChartViewConfig(viewConfig)
    const k = String(key)
    const set = new Set(cfg.hiddenSeries)
    if (set.has(k)) set.delete(k)
    else set.add(k)
    return { ...cfg, hiddenSeries: [...set] }
}

export function topSeriesKeys(payload, limit = 5) {
    const legend = payload?.legend || []
    const indexed = (payload?.series || []).map((series, idx) => ({
        key: seriesKey(series, idx, legend[idx]),
        total: seriesTotal(series),
    }))
    return new Set(
        [...indexed].sort((a, b) => b.total - a.total).slice(0, limit).map(e => e.key),
    )
}

export function isTop5PresetActive(payload, viewConfig) {
    const cfg = normalizeChartViewConfig(viewConfig)
    const keys = allSeriesKeys(payload)
    if (keys.length <= 5) return cfg.hiddenSeries.length === 0
    const top = topSeriesKeys(payload, 5)
    const hidden = new Set(cfg.hiddenSeries.map(String))
    const visible = keys.filter(k => !hidden.has(k))
    return visible.length === 5 && visible.every(k => top.has(k))
}

export function applyTop5Preset(payload, viewConfig) {
    const cfg = normalizeChartViewConfig(viewConfig)
    const keys = allSeriesKeys(payload)
    const top = topSeriesKeys(payload, 5)
    return {
        ...cfg,
        hiddenSeries: keys.filter(k => !top.has(k)).map(String),
    }
}

export function clearTop5Preset(viewConfig) {
    return { ...normalizeChartViewConfig(viewConfig), hiddenSeries: [] }
}

export function toggleTop5Preset(payload, viewConfig) {
    if (isTop5PresetActive(payload, viewConfig)) {
        return clearTop5Preset(viewConfig)
    }
    return applyTop5Preset(payload, viewConfig)
}

/** Пункты легенды: «Все», «Топ-5», серии. */
export function buildLegendItems(payload, viewConfig) {
    const displayPayload = resolveChartPayload(payload, viewConfig)
    const cfg = normalizeChartViewConfig(viewConfig)
    const legend = displayPayload?.legend || []
    const series = displayPayload?.series || []
    const hidden = new Set(cfg.hiddenSeries.map(String))
    const top5On = isTop5PresetActive(displayPayload, cfg)

    const seriesItems = series.map((s, idx) => {
        const item = legend[idx] || {}
        const key = seriesKey(s, idx, item)
        return {
            key,
            label: item.label || item.name || s.id || String(idx),
            color: item.color || s.color,
            style: item.style || s.style,
            hidden: hidden.has(key),
            dimmed: false,
        }
    })

    const allHidden = seriesItems.length > 0 && seriesItems.every(i => i.hidden)

    return [
        {
            key: '__all__',
            label: 'Все',
            isSelectAll: true,
            checked: !allHidden,
            hidden: false,
        },
        {
            key: '__top5__',
            label: 'Топ-5',
            isTop5: true,
            active: top5On,
            hidden: false,
        },
        ...seriesItems,
    ]
}

export function toggleLegendKey(payload, viewConfig, key) {
    const displayPayload = resolveChartPayload(payload, viewConfig)
    const cfg = normalizeChartViewConfig(viewConfig)
    if (key === '__all__') {
        const keys = allSeriesKeys(displayPayload)
        const allHidden = keys.length > 0 && keys.every(k => cfg.hiddenSeries.includes(String(k)))
        return { ...cfg, hiddenSeries: allHidden ? [] : keys.map(String) }
    }
    if (key === '__top5__') {
        return toggleTop5Preset(displayPayload, cfg)
    }
    return toggleHiddenSeries(cfg, key)
}
